import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { t, languages } from "@/i18n";

export default function Register() {
  const { register } = useAuth();
  const { lang, setLang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Auto-fill referral code from URL: /register?ref=NVC-XXXXXX (run once on mount)
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      toast.success(t(lang, "referral_applied"), { duration: 4000 });
    }
    // Intentionally run once on mount; we don't want to re-fire toast if lang changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password, lang, referralCode.trim() || undefined);
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(lang, "error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.pexels.com/photos/7624975/pexels-photo-7624975.jpeg"
          alt="Dialogue"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/20" />
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
              <Leaf className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-semibold">{t(lang, "app_name")}</span>
          </Link>
          <h1 className="font-heading text-3xl font-medium">{t(lang, "register")}</h1>
          <div className="space-y-2">
            <Label>{t(lang, "name")}</Label>
            <Input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="p-4" />
          </div>
          <div className="space-y-2">
            <Label>{t(lang, "email")}</Label>
            <Input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="p-4" />
          </div>
          <div className="space-y-2">
            <Label>{t(lang, "password")}</Label>
            <Input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="p-4" />
          </div>
          <div className="space-y-2">
            <Label>{t(lang, "language")}</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="register-lang"><SelectValue /></SelectTrigger>
              <SelectContent>
                {languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
              {t(lang, "referral_code_optional")}
            </Label>
            <Input
              data-testid="register-referral"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="NVC-XXXXXX"
              className="p-4 uppercase font-mono tracking-wider"
              maxLength={12}
            />
          </div>
          <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full rounded-full">
            {t(lang, "register")}
          </Button>
          <div className="text-sm text-muted-foreground">
            {t(lang, "have_account")} <Link to="/login" className="text-primary underline" data-testid="link-login">{t(lang, "login")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
